import { useNavigate } from 'react-router-dom';

import { MetricCard } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { offeringLabel } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import { SEATS_TAB, SETTINGS_TAB_PARAM } from '@backrs_ui/grow-ui/src/pages/Settings/settingsTabs';
import type { AdminRoster } from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { Box, Button, Chip, type SxProps, type Theme } from '@mui/material';

const strings = appStrings.modules.rostersAdmin;

export type SeatTotal = Readonly<{
  /** Kebab-case, so it can carry a DOM id. */
  key: string;
  label: string;
  kind: string | null;
  seatsUsed: number;
  /** Null where any roster in the group is uncapped, which leaves the group's total unknown. */
  capacity: number | null;
}>;

const slug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * Rosters sharing an offering add together, first appearance first. Offerings carrying no id
 * group by label, so rosters whose offering has neither id nor name merge under one card.
 */
export const seatTotals = (rosters: readonly AdminRoster[]): SeatTotal[] => {
  const byOffering = new Map<string, SeatTotal>();

  rosters.forEach((roster) => {
    const label = offeringLabel(roster.offering);
    const key = roster.offering.id === null ? slug(label) : String(roster.offering.id);
    const running = byOffering.get(key);

    byOffering.set(
      key,
      running === undefined
        ? {
            key,
            label,
            kind: roster.offering.kind,
            seatsUsed: roster.seatsUsed,
            capacity: roster.capacity,
          }
        : {
            ...running,
            seatsUsed: running.seatsUsed + roster.seatsUsed,
            capacity:
              running.capacity === null || roster.capacity === null
                ? null
                : running.capacity + roster.capacity,
          },
    );
  });

  return Array.from(byOffering.values());
};

export const grandTotalSeats = (totals: readonly SeatTotal[]): number =>
  totals.reduce((running, total) => running + total.seatsUsed, 0);

/** Null where the group is uncapped or holds no seats, neither of which yields a share. */
export const percentUsed = ({ seatsUsed, capacity }: SeatTotal): number | null =>
  capacity === null || capacity === 0 ? null : Math.round((seatsUsed / capacity) * 100);

const STAFF_OFFERING_KIND = 'staff';
const COACH_OFFERING_KIND = 'coach';

/**
 * Staff seats ride on the coach card rather than getting one of their own. Without a coach
 * card to carry them they keep their own, so the count is never dropped from the page.
 */
export const seatCards = (
  rosters: readonly AdminRoster[],
): Readonly<{ cards: SeatTotal[]; staff: SeatTotal | null; coachKey: string | null }> => {
  const totals = seatTotals(rosters);
  const staff = totals.find((total) => total.kind === STAFF_OFFERING_KIND) ?? null;
  const coach = totals.find((total) => total.kind === COACH_OFFERING_KIND) ?? null;

  if (staff === null || coach === null) return { cards: totals, staff: null, coachKey: null };

  return {
    cards: totals.filter((total) => total !== staff),
    staff,
    coachKey: coach.key,
  };
};

export const seatsAgentContext = (rosters: readonly AdminRoster[]) => {
  const { cards, staff } = seatCards(rosters);

  return {
    total_seats_used: grandTotalSeats(seatTotals(rosters)),
    staff_seats_used: staff?.seatsUsed ?? null,
    programs: cards.map((total) => ({
      name: total.label,
      seats_used: total.seatsUsed,
      capacity: total.capacity,
      percent_used: percentUsed(total),
    })),
  };
};

const UsageChip = ({ total }: Readonly<{ total: SeatTotal }>) => {
  const percent = percentUsed(total);

  if (percent === null) return null;

  return (
    <Chip
      id={`rosters-admin-seat-total-${total.key}-percent`}
      variant="tag"
      size="sm"
      label={strings.seatSummary.percentUsed(percent)}
      aria-label={strings.seatSummary.percentUsedAria(total.label, percent)}
      sx={chipSx}
    />
  );
};

const CardChips = ({ total, staff }: Readonly<{ total: SeatTotal; staff: SeatTotal | null }>) => (
  <Box sx={chipRowSx}>
    {staff === null ? null : (
      <Chip
        id={`rosters-admin-seat-total-${total.key}-staff`}
        variant="tag"
        size="sm"
        label={strings.seatSummary.staffSeats(staff.seatsUsed)}
        sx={chipSx}
      />
    )}
    <UsageChip total={total} />
  </Box>
);

export const SeatSummary = ({
  rosters,
  showContractLink = false,
}: Readonly<{
  rosters: readonly AdminRoster[];
  showContractLink?: boolean;
}>) => {
  const navigate = useNavigate();
  const totals = seatTotals(rosters);
  const { cards, staff, coachKey } = seatCards(rosters);

  if (totals.length === 0) return null;

  return (
    <Box component="section" aria-label={strings.seatSummary.regionAria} sx={rowSx}>
      <Box sx={cardWidthSx}>
        <MetricCard
          id="rosters-admin-seat-total-all"
          label={strings.seatSummary.total}
          value={grandTotalSeats(totals)}
          footer={
            showContractLink ? (
              <Button
                id="rosters-admin-seat-summary-contract"
                hierarchy="Secondary gray"
                size="sm"
                onClick={() => navigate(`${APP_ROUTE.SETTINGS}?${SETTINGS_TAB_PARAM}=${SEATS_TAB}`)}
              >
                {strings.seatSummary.contractLink}
              </Button>
            ) : null
          }
        />
      </Box>
      {cards.map((total) => (
        <Box key={total.key} sx={cardWidthSx}>
          <MetricCard
            id={`rosters-admin-seat-total-${total.key}`}
            label={total.label}
            value={total.seatsUsed}
            footer={<CardChips total={total} staff={total.key === coachKey ? staff : null} />}
          />
        </Box>
      ))}
    </Box>
  );
};

const chipRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-primary'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  boxShadow: backrs.shadows['shadow-xs'],
  paddingTop: backrs.spacing['spacing-none'],
  paddingBottom: backrs.spacing['spacing-none'],
  '& .MuiChip-label': {
    color: backrs.colors['Colors/Text/text-secondary'],
  },
});

const rowSx: SxProps<Theme> = ({ backrs }) => ({
  display: { xs: 'none', md: 'flex' },
  flexWrap: 'nowrap',
  alignItems: 'flex-end',
  justifyContent: 'flex-start',
  flexShrink: 0,
  gap: backrs.spacing['spacing-xl'],
});

const cardWidthSx: SxProps<Theme> = {
  flex: '0 0 auto',
  minWidth: 250,
};
